<template>
  <div class="sp-card" :class="classModifiers" part="root">
    <div class="sp-card__content-overlay">
      <slot name="content-overlay" />
    </div>
    <div class="sp-card__title">
      <slot name="title">
        <sp-card-title v-if="title">{{ title }}</sp-card-title>
      </slot>
    </div>
    <div class="sp-card__subtitle">
      <slot name="subtitle">
        <sp-card-subtitle v-if="subtitle">{{ subtitle }}</sp-card-subtitle>
      </slot>
    </div>
    <div class="sp-card__content">
      <slot />
    </div>
    <div class="sp-card__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useVariant } from "../../composables/variant";
import { toBoolean } from "../../utils/props";

const props = defineProps({
  title: {
    type: String,
    default: undefined,
  },
  subtitle: {
    type: String,
    default: undefined,
  },
  scrollableContent: {
    type: [Boolean, String],
    default: false,
  },
  grid: {
    type: [Boolean, String],
    default: false,
  },
  variant: {
    type: String,
    default: "plain",
    validator: (value) => ["text", "flat", "elevated", "tonal", "outlined", "plain"].includes(value),
  },
  smVariant: {
    type: String,
    default: undefined,
    validator: (value) => ["text", "flat", "elevated", "tonal", "outlined", "plain"].includes(value),
  },
  /**
   * Designates an elevation applied to the component between 1 and 2.
   */
  elevation: {
    type: Number,
    default: undefined,
    validator: (value) => [1, 2].includes(value),
  },
});
const { variantModifiers } = useVariant(props);
const classModifiers = computed(() => [
  {
    "--grid": toBoolean(props.grid) || toBoolean(props.scrollableContent),
    "--scrollable-content": toBoolean(props.scrollableContent),
    [`--elevation-${props.elevation}`]: props.elevation,
  },
  ...variantModifiers.value,
]);
</script>

<style>
:host {
  --card-border: var(--sp-comp-card-border, none);
  display: block;
  overflow: visible;
  height: var(--sp-card-root-height, auto);
}
</style>

<style scoped lang="scss">
.sp-card {
  --card-title-z-index: var(--sp-comp-card-title-z-index, 99);

  border: var(--card-border);
  border-radius: var(--sp-comp-card-border-radius, var(--sp-sys-form-field-border-radius));
  box-sizing: border-box;
  overflow: hidden;

  &.--grid {
    display: grid;
    grid-template-rows: auto auto 1fr auto;
    grid-template-areas: "title" "subtitle" "content" "actions";
    height: var(--sp-comp-card-height, auto);

    .sp-card__content {
      position: relative;
      overflow-y: hidden;
    }
  }

  &.--scrollable-content {
    .sp-card__content {
      overflow-y: auto !important;
    }
  }

  &.--variant-outlined {
    border: var(--sp-comp-card-outlined-border, 1px solid var(--sp-sys-color-outline));
  }

  &.--elevation-1,
  &.--variant-elevated {
    box-shadow: var(--sp-sys-elevation-1);
  }

  &.--elevation-2 {
    box-shadow: var(--sp-sys-elevation-2);
  }
  // Sets card padding to 0
  &.--variant-plain {
    --sp-comp-card-padding-inline: 0;
    --sp-comp-card-padding-block: 0;
  }
  // Removes card shadow and border
  &.--variant-plain,
  &.--variant-flat {
    box-shadow: none !important;
    border: none !important;
  }
}

.sp-card__title {
  grid-area: title;
  z-index: var(--card-title-z-index);
}

.sp-card__subtitle {
  grid-area: subtitle;
}

.sp-card__content {
  grid-area: content;
  position: relative;
  height: var(--sp-comp-card-content-height, 100%);
}

.sp-card__actions {
  grid-area: actions;
}

.sp-card__content-overlay {
  z-index: calc(var(--card-title-z-index) - 1);
}
</style>
